import { watch } from "vue";
import { useUserStore } from "@/stores/user.store";
import { useMutation } from "@urql/vue";
import { graphql as gql } from "@/generated";
import { isGpcEnabled } from "@/utils/gpc";

const UpdateDoNotSellFromGpcDocument = gql(/* GraphQL */ `
  mutation UpdateDoNotSellFromGpc($ccpa_do_not_sell: Boolean!) {
    updateMe(ccpa_do_not_sell: $ccpa_do_not_sell) {
      ...UserStoreMe
    }
  }
`);

/**
 * Composable that monitors privacy signals (e.g., Global Privacy Control)
 * and automatically updates user privacy preferences when detected.
 *
 * This composable:
 * - Detects GPC signal via navigator.globalPrivacyControl
 * - Only acts on authenticated users
 * - Sets ccpa_do_not_sell to true when GPC is enabled
 * - Skips users who already have ccpa_do_not_sell: true
 * - One-way ratchet: GPC sets but never reverts the preference
 * - Logs the change to consent audit trail with source: "gpc_signal"
 *
 * Should be called once during app initialization (in app.vue).
 */
export function usePrivacySignals() {
  const userStore = useUserStore();
  const { executeMutation } = useMutation(UpdateDoNotSellFromGpcDocument);

  // Watch for authenticated user changes
  watch(
    () => userStore.user,
    async (user) => {
      // Only process authenticated users
      if (!user) return;

      // Skip if user already has ccpa_do_not_sell enabled
      if (user.ccpa_do_not_sell === true) return;

      // Check if GPC is enabled
      const gpcEnabled = isGpcEnabled();
      if (!gpcEnabled) return;

      // GPC is enabled and user hasn't opted out yet - update preference
      console.log(
        "[PrivacySignals] GPC detected, setting ccpa_do_not_sell=true",
      );

      try {
        const result = await executeMutation({
          ccpa_do_not_sell: true,
        });

        if (result.error) {
          console.error(
            "[PrivacySignals] Failed to update GPC preference:",
            result.error,
          );
          return;
        }

        // The mutation response includes the full user with UserStoreMe fragment,
        // which will automatically update the URQL cache and the user store
        console.log("[PrivacySignals] GPC preference updated successfully");
      } catch (error) {
        console.error(
          "[PrivacySignals] Exception while updating GPC preference:",
          error,
        );
      }
    },
    { immediate: true },
  );
}
